// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * App orchestration: lobby → call. Owns app state, wires the DOM built in ui.ts
 * to the call controller, mic capture, metering, push-to-talk and the recorder.
 * No heavy CPU work here — a live call is inherently event-driven and
 * main-thread.
 */
import './styles/main.css';
import QRCode from 'qrcode';
import { shellHtml, lobbyHtml, callHtml, makeCard, initials, escapeHtml } from './ui.ts';
import { icon } from './icons.ts';
import { initModals, openModal, closeModal, isModalOpen, toast, howItWorksHtml, privacyHtml, aboutHtml } from './modals.ts';
import { initGlossary } from './glossary.ts';
import { emit, mountEventDrawer } from './eventlog.ts';
import { CallController } from './call.ts';
import { getMicStream, listMics, stopStream, AudioMeter, isIPhone } from './media.ts';
import { CallRecorder, recorderSupported, extForMime, fmtDur } from './recorder.ts';
import { shouldTransmit, micButtonLabel, otherMode } from './ptt.ts';
import { isSpeaking } from './mesh.ts';
import { deriveSasWords } from './crypto.ts';
import { generateRoomId, generatePassphrase, isValidRoomId, normalizePass } from './room.ts';
import { readInvite, writeRoomHash, currentInviteUrl } from './router.ts';
import type { Participant, Prefs, Presence, TalkMode } from './types.ts';

interface Card {
  el: HTMLElement;
  meter?: AudioMeter;
  audio?: HTMLAudioElement;
}

const PREFS_KEY = 'talkwell:prefs';

const state = {
  mode: 'create' as 'create' | 'join',
  room: '',
  pass: '',
  includePass: true,
  prefs: loadPrefs(),
  talkMode: 'open' as TalkMode,
  muted: false, // open-mic self-mute
  held: false, // ptt talk button / Space held
  transmitting: false,
  listenOnly: false,
  localStream: null as MediaStream | null,
  controller: null as CallController | null,
  audioCtx: null as AudioContext | null,
  participants: new Map<string, Participant>(),
  cards: new Map<string, Card>(),
  recorder: null as CallRecorder | null,
  recording: false,
  recordStartedAt: 0,
  callStartedAt: 0,
  loop: null as ReturnType<typeof setInterval> | null,
  wakeLock: null as WakeLockSentinel | null,
  drawerDispose: null as (() => void) | null,
};

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) return { name: '', mode: 'open', ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { name: '', mode: 'open' };
}
function savePrefs(): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(state.prefs));
  } catch {
    /* ignore */
  }
}

// ─── Boot ────────────────────────────────────────────────────────────────
function boot(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = shellHtml();
  initModals();
  initGlossary(document.body);
  wireShell();

  state.talkMode = state.prefs.mode === 'ptt' ? 'ptt' : 'open';

  const invite = readInvite();
  if (invite) {
    state.mode = 'join';
    state.room = invite.room;
    state.pass = invite.pass ?? '';
  } else {
    state.mode = 'create';
    state.room = generateRoomId();
    state.pass = generatePassphrase();
  }
  renderLobby();
  emit('system', 'ok', 'Talkwell ready — nothing has left your device', { mode: state.mode });
}

function wireShell(): void {
  document.querySelectorAll<HTMLElement>('[data-modal]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const which = btn.dataset.modal;
      if (which === 'how') openModal('How Talkwell works', howItWorksHtml());
      else if (which === 'privacy') openModal('Privacy & threat model', privacyHtml());
      else if (which === 'about') openModal('About Talkwell', aboutHtml());
    });
  });
  document.getElementById('log-toggle')?.addEventListener('click', toggleDrawer);
}

// ─── Event log drawer ───────────────────────────────────────────────────
function toggleDrawer(): void {
  const drawer = document.getElementById('drawer') as HTMLElement;
  if (drawer.hidden) {
    drawer.hidden = false;
    if (!state.drawerDispose) state.drawerDispose = mountEventDrawer(drawer);
    drawer.classList.add('open');
    drawer.querySelector<HTMLElement>('#drawer-close')?.addEventListener('click', closeDrawer, { once: true });
  } else {
    closeDrawer();
  }
}
function closeDrawer(): void {
  const drawer = document.getElementById('drawer') as HTMLElement;
  drawer.classList.remove('open');
  drawer.hidden = true;
}
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const drawer = document.getElementById('drawer') as HTMLElement | null;
    if (drawer && !drawer.hidden && !isModalOpen()) closeDrawer();
  }
});

// ─── Lobby ───────────────────────────────────────────────────────────────
function renderLobby(): void {
  const view = document.getElementById('view')!;
  view.innerHTML = lobbyHtml({ mode: state.mode, room: state.room, name: state.prefs.name, talkMode: state.talkMode });
  view.classList.remove('in-call');

  const nameInput = document.getElementById('name-input') as HTMLInputElement;
  nameInput.addEventListener('input', () => {
    state.prefs.name = nameInput.value.trim();
  });

  document.querySelectorAll<HTMLElement>('.mode-opt[data-mode]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.talkMode = btn.dataset.mode === 'ptt' ? 'ptt' : 'open';
      state.prefs.mode = state.talkMode;
      savePrefs();
      document.querySelectorAll<HTMLElement>('.mode-opt[data-mode]').forEach((b) => {
        const on = b.dataset.mode === state.talkMode;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', String(on));
      });
    });
  });

  (document.getElementById('join-btn') as HTMLButtonElement).addEventListener('click', () => void enterCall());
  document.getElementById('join-instead')?.addEventListener('click', promptJoinCode);
  document.getElementById('start-instead')?.addEventListener('click', () => {
    state.mode = 'create';
    state.room = generateRoomId();
    state.pass = generatePassphrase();
    history.replaceState(null, '', location.pathname + location.search);
    renderLobby();
    void startMicCheck();
  });

  if (isIPhone()) {
    const n = document.getElementById('ios-note');
    if (n) n.hidden = false;
  }

  void startMicCheck();
}

// The lobby mic check: a live level bar so people know the mic works before joining.
let lobbyMeter: AudioMeter | null = null;
let lobbyLoop: ReturnType<typeof setInterval> | null = null;
let lobbyCtx: AudioContext | null = null;

async function startMicCheck(): Promise<void> {
  stopMicCheck();
  const hint = document.getElementById('mic-hint');
  const orb = document.getElementById('mic-orb');
  try {
    stopStream(state.localStream);
    state.localStream = await getMicStream(state.prefs.micId);
    state.listenOnly = false;
    if (hint) hint.textContent = 'Microphone ready — say something and watch the bar.';
    orb?.classList.remove('off');
    try {
      lobbyCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      lobbyMeter = new AudioMeter(lobbyCtx, state.localStream);
      const fill = document.getElementById('mic-meter-fill') as HTMLElement | null;
      lobbyLoop = setInterval(() => {
        const lvl = lobbyMeter?.read() ?? 0;
        if (fill) fill.style.width = `${Math.round(lvl * 100)}%`;
        orb?.classList.toggle('live', lvl > 0.12);
      }, 100);
    } catch {
      /* metering optional */
    }
    await populateMics();
    emit('media', 'ok', 'Microphone ready (local check only)');
  } catch (err) {
    handlePermissionDenied(err as Error);
  }
}

function stopMicCheck(): void {
  if (lobbyLoop) clearInterval(lobbyLoop);
  lobbyLoop = null;
  lobbyMeter?.close();
  lobbyMeter = null;
  try {
    void lobbyCtx?.close();
  } catch {
    /* ignore */
  }
  lobbyCtx = null;
}

async function populateMics(): Promise<void> {
  try {
    const mics = await listMics();
    const sel = document.getElementById('mic-select') as HTMLSelectElement | null;
    if (!sel) return;
    sel.innerHTML = `<option value="">Default microphone</option>`;
    mics.forEach((d, i) => {
      const opt = document.createElement('option');
      opt.value = d.deviceId;
      opt.textContent = d.label || `Microphone ${i + 1}`;
      if (d.deviceId === state.prefs.micId) opt.selected = true;
      sel.appendChild(opt);
    });
    sel.addEventListener('change', () => {
      state.prefs.micId = sel.value || undefined;
      savePrefs();
      void startMicCheck();
    });
  } catch {
    /* enumerate can fail before permission; ignore */
  }
}

function handlePermissionDenied(err: Error): void {
  state.listenOnly = true;
  stopStream(state.localStream);
  state.localStream = null;
  const note = document.getElementById('perm-note');
  if (note) {
    note.hidden = false;
    note.innerHTML = `${escapeHtml(err.name === 'NotAllowedError' ? 'Microphone blocked.' : 'No microphone available.')} You can still join <strong>listen-only</strong> to hear everyone.`;
  }
  document.getElementById('mic-orb')?.classList.add('off');
  const hint = document.getElementById('mic-hint');
  if (hint) hint.textContent = 'Listen-only: you’ll hear the call but won’t transmit.';
  emit('media', 'warn', 'Microphone unavailable — listen-only join offered', { error: err.name });
}

function promptJoinCode(): void {
  const code = window.prompt('Paste an invite link, or type a room code:');
  if (!code) return;
  const invite = readInviteFrom(code);
  if (!invite) {
    toast('That doesn’t look like a valid link or code', 'err');
    return;
  }
  state.mode = 'join';
  state.room = invite.room;
  state.pass = invite.pass ?? '';
  history.replaceState(null, '', `#room=${encodeURIComponent(invite.room)}${invite.pass ? `&pass=${encodeURIComponent(invite.pass)}` : ''}`);
  renderLobby();
}

function readInviteFrom(text: string): { room: string; pass?: string } | null {
  const t = text.trim();
  if (t.includes('#') || t.includes('room=')) {
    const hashIdx = t.indexOf('#');
    const hash = hashIdx >= 0 ? t.slice(hashIdx + 1) : t;
    const params = new URLSearchParams(hash);
    const room = params.get('room')?.trim();
    if (room && isValidRoomId(room)) return { room, pass: params.get('pass')?.trim() || undefined };
  }
  if (isValidRoomId(t)) return { room: t };
  return null;
}

// ─── Enter the call ──────────────────────────────────────────────────────
async function enterCall(): Promise<void> {
  const joinBtn = document.getElementById('join-btn') as HTMLButtonElement | null;
  if (joinBtn) joinBtn.disabled = true;
  savePrefs();
  stopMicCheck();

  // Ensure we have (or knowingly lack) a mic captured inside this tap.
  if (!state.localStream && !state.listenOnly) {
    try {
      state.localStream = await getMicStream(state.prefs.micId);
    } catch {
      state.listenOnly = true;
    }
  }

  try {
    state.audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    await state.audioCtx.resume().catch(() => undefined);
  } catch {
    state.audioCtx = null;
  }

  // Initial transmit state: open mic → live immediately; ptt → silent until held.
  state.muted = false;
  state.held = false;
  applyTransmitGate(true);

  const presence = currentPresence();
  state.controller = new CallController(state.room, normalizePass(state.pass), presence, {
    onPeerJoin: (id) => onPeerJoin(id),
    onPeerLeave: (id) => onPeerLeave(id),
    onStream: (id, s) => onStream(id, s),
    onPresence: (id, p) => onPresence(id, p),
    onChat: (id, m) => onChat(id, m.text),
    onError: (msg) => {
      toast(msg, 'err');
      emit('signal', 'err', msg);
    },
  });

  renderCall();
  state.controller.join(state.localStream);
  state.callStartedAt = Date.now();
  writeRoomHash(state.room, state.pass, state.includePass);
  emit('signal', 'ok', 'Joined room — waiting for peers', { room: state.room, mode: state.talkMode });
  await deriveSas();
  startLoop();
  void acquireWakeLock();

  if (state.mode === 'create') setTimeout(() => openInviteModal(), 400);
}

function currentPresence(): Presence {
  return {
    name: state.prefs.name || 'Guest',
    live: state.transmitting,
    recording: state.recording,
    mode: state.talkMode,
  };
}

async function deriveSas(): Promise<void> {
  try {
    const words = await deriveSasWords(normalizePass(state.pass), state.room);
    const bar = document.getElementById('sas-bar');
    const el = document.getElementById('sas-words');
    if (el) el.textContent = words.join(' · ');
    if (bar) bar.hidden = false;
    emit('crypto', 'ok', 'Verification words derived from passphrase', { words: words.join(' ') });
  } catch {
    emit('crypto', 'warn', 'Could not derive verification words');
  }
}

// ─── Transmit gate (open mic / push-to-talk) ─────────────────────────────
function applyTransmitGate(force = false): void {
  const tx = shouldTransmit({ mode: state.talkMode, muted: state.muted, held: state.held, listenOnly: state.listenOnly });
  state.localStream?.getAudioTracks().forEach((t) => (t.enabled = tx));
  const changed = tx !== state.transmitting;
  state.transmitting = tx;
  updateTalkButton();
  updateSelfCard();
  if (changed || force) {
    if (!force) broadcast();
  }
}

function updateTalkButton(): void {
  const btn = document.getElementById('talk-btn') as HTMLButtonElement | null;
  const label = document.getElementById('talk-label');
  if (!btn) return;
  btn.classList.toggle('ptt', state.talkMode === 'ptt');
  btn.classList.toggle('live', state.transmitting);
  btn.classList.toggle('muted', !state.transmitting && !state.listenOnly);
  btn.disabled = state.listenOnly;
  btn.setAttribute('aria-pressed', String(state.transmitting));
  const iconEl = btn.querySelector('.talk-icon');
  if (iconEl) iconEl.innerHTML = icon(state.transmitting || state.talkMode === 'ptt' ? 'mic' : 'micOff');
  if (label) label.textContent = micButtonLabel(state.talkMode, state.transmitting, state.listenOnly);
}

// ─── Call view ───────────────────────────────────────────────────────────
function renderCall(): void {
  const view = document.getElementById('view')!;
  view.innerHTML = callHtml(state.talkMode);
  view.classList.add('in-call');

  // Self card
  const selfEl = makeCard('self', state.prefs.name || 'Guest', true);
  document.getElementById('grid')!.appendChild(selfEl);
  const selfCard: Card = { el: selfEl };
  if (state.audioCtx && state.localStream) selfCard.meter = new AudioMeter(state.audioCtx, state.localStream);
  state.cards.set('self', selfCard);
  updateSelfCard();

  // Talk button — behaves as a hold button in PTT, a toggle in open mic.
  const talkBtn = document.getElementById('talk-btn') as HTMLButtonElement;
  talkBtn.addEventListener('pointerdown', onTalkDown);
  talkBtn.addEventListener('pointerup', onTalkUp);
  talkBtn.addEventListener('pointerleave', onTalkUp);
  talkBtn.addEventListener('pointercancel', onTalkUp);
  talkBtn.addEventListener('click', onTalkClick);
  talkBtn.addEventListener('contextmenu', (e) => e.preventDefault());

  document.getElementById('btn-mode')!.addEventListener('click', switchMode);
  const recBtn = document.getElementById('btn-record') as HTMLButtonElement;
  if (!recorderSupported()) recBtn.hidden = true;
  else recBtn.addEventListener('click', () => void toggleRecord());
  document.getElementById('btn-leave')!.addEventListener('click', leaveCall);
  document.getElementById('invite-btn')!.addEventListener('click', openInviteModal);
  document.getElementById('btn-people')!.addEventListener('click', () => openSide('people'));
  document.getElementById('btn-chat')!.addEventListener('click', () => openSide('chat'));
  document.getElementById('side-close')!.addEventListener('click', closeSide);
  document.querySelectorAll<HTMLElement>('.side-tab[data-side]').forEach((t) =>
    t.addEventListener('click', () => openSide(t.dataset.side as 'people' | 'chat')),
  );
  document.getElementById('chat-form')!.addEventListener('submit', onChatSubmit);

  if (isIPhone()) {
    const n = document.getElementById('ios-inline');
    if (n) n.hidden = false;
  }

  updateTalkButton();
  refreshLayout();
  refreshPeople();
}

// PTT press / release (pointer). In open mode these are no-ops; click toggles instead.
function onTalkDown(e: Event): void {
  if (state.talkMode !== 'ptt' || state.listenOnly) return;
  e.preventDefault();
  if (!state.held) {
    state.held = true;
    applyTransmitGate();
  }
}
function onTalkUp(): void {
  if (state.talkMode !== 'ptt') return;
  if (state.held) {
    state.held = false;
    applyTransmitGate();
  }
}
function onTalkClick(): void {
  if (state.talkMode !== 'open' || state.listenOnly) return;
  state.muted = !state.muted;
  applyTransmitGate();
  emit('media', 'info', state.muted ? 'Microphone muted' : 'Microphone live');
}

function switchMode(): void {
  state.talkMode = otherMode(state.talkMode);
  state.prefs.mode = state.talkMode;
  savePrefs();
  // Reset gating for the new mode: open → live, ptt → silent until held.
  state.muted = false;
  state.held = false;
  const label = document.getElementById('mode-btn-label');
  if (label) label.textContent = state.talkMode === 'ptt' ? 'Open mic' : 'Walkie';
  applyTransmitGate();
  emit('ui', 'info', `Switched to ${state.talkMode === 'ptt' ? 'walkie-talkie' : 'open mic'}`);
  toast(state.talkMode === 'ptt' ? 'Walkie-talkie: hold to talk (or Space)' : 'Open mic: everyone’s live', 'info');
}

// Space bar as push-to-talk.
document.addEventListener('keydown', (e) => {
  if (e.code !== 'Space' || e.repeat) return;
  if (state.talkMode !== 'ptt' || state.listenOnly || !state.callStartedAt) return;
  if (isTypingTarget(e.target) || isModalOpen()) return;
  e.preventDefault();
  if (!state.held) {
    state.held = true;
    applyTransmitGate();
  }
});
document.addEventListener('keyup', (e) => {
  if (e.code !== 'Space') return;
  if (state.talkMode !== 'ptt') return;
  if (state.held) {
    state.held = false;
    applyTransmitGate();
  }
});
function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

// ─── Participants ────────────────────────────────────────────────────────
function ensureParticipant(id: string): Participant {
  let p = state.participants.get(id);
  if (!p) {
    p = { id, name: 'Guest', state: 'connecting', live: false, recording: false, mode: 'open', level: 0, joinedAt: Date.now() };
    state.participants.set(id, p);
    const el = makeCard(id, p.name, false);
    document.getElementById('grid')?.appendChild(el);
    state.cards.set(id, { el });
    refreshLayout();
  }
  return p;
}

function onPeerJoin(id: string): void {
  const p = ensureParticipant(id);
  p.state = 'connected';
  emit('peer', 'ok', 'Peer joined', { peer: short(id) });
  refreshPeople();
  refreshRecorderAudio();
}

function onPeerLeave(id: string): void {
  const p = state.participants.get(id);
  if (p) emit('peer', 'warn', `${p.name || 'A peer'} left`, { peer: short(id) });
  state.participants.delete(id);
  const card = state.cards.get(id);
  card?.meter?.close();
  if (card?.audio) {
    card.audio.srcObject = null;
    card.audio.remove();
  }
  card?.el.remove();
  state.cards.delete(id);
  refreshLayout();
  refreshPeople();
  refreshRecorderAudio();
}

function onStream(id: string, stream: MediaStream): void {
  const p = ensureParticipant(id);
  p.stream = stream;
  const card = state.cards.get(id);
  if (card) {
    // A dedicated <audio> element actually plays the peer's voice.
    if (!card.audio) {
      const a = document.createElement('audio');
      a.autoplay = true;
      (a as HTMLAudioElement & { playsInline: boolean }).playsInline = true;
      a.className = 'peer-audio';
      card.el.appendChild(a);
      card.audio = a;
    }
    card.audio.srcObject = stream;
    card.audio.play?.().catch(() => undefined);
    card.meter?.close();
    if (state.audioCtx) card.meter = new AudioMeter(state.audioCtx, stream);
  }
  emit('media', 'ok', 'Receiving voice from peer', { peer: short(id) });
  updatePeerCard(id);
  refreshRecorderAudio();
}

function onPresence(id: string, presence: Presence): void {
  const p = ensureParticipant(id);
  p.name = presence.name || 'Guest';
  p.live = presence.live;
  p.recording = presence.recording;
  p.mode = presence.mode === 'ptt' ? 'ptt' : 'open';
  p.state = 'connected';
  updatePeerCard(id);
  refreshPeople();
}

function updateSelfCard(): void {
  const card = state.cards.get('self');
  if (!card) return;
  const name = state.prefs.name || 'Guest';
  (card.el.querySelector('.orb-avatar') as HTMLElement).textContent = initials(name);
  (card.el.querySelector('.speaker-name') as HTMLElement).textContent = `${name} (you)`;
  const micEl = card.el.querySelector('.orb-mic') as HTMLElement;
  const off = state.listenOnly || (!state.transmitting && state.talkMode === 'open');
  micEl.classList.toggle('muted', off);
  micEl.innerHTML = icon(off ? 'micOff' : 'mic');
  const st = card.el.querySelector('.speaker-state') as HTMLElement;
  const text = state.listenOnly ? 'listening' : state.transmitting ? 'transmitting' : state.talkMode === 'ptt' ? 'idle' : 'muted';
  st.textContent = text;
  st.hidden = false;
  card.el.classList.toggle('tx', state.transmitting);
  card.el.classList.toggle('idle-card', !state.transmitting);
}

function updatePeerCard(id: string): void {
  const card = state.cards.get(id);
  const p = state.participants.get(id);
  if (!card || !p) return;
  (card.el.querySelector('.orb-avatar') as HTMLElement).textContent = initials(p.name);
  (card.el.querySelector('.speaker-name') as HTMLElement).textContent = p.name || 'Guest';
  const micEl = card.el.querySelector('.orb-mic') as HTMLElement;
  micEl.classList.toggle('muted', !p.live);
  micEl.innerHTML = icon(p.live ? 'mic' : 'micOff');
  const st = card.el.querySelector('.speaker-state') as HTMLElement;
  st.textContent = p.recording ? 'recording' : p.live ? (p.mode === 'ptt' ? 'talking' : 'live') : p.mode === 'ptt' ? 'listening' : 'muted';
  st.hidden = false;
}

function refreshLayout(): void {
  const grid = document.getElementById('grid');
  if (!grid) return;
  grid.dataset.count = String(state.cards.size);
}

function refreshPeople(): void {
  const body = document.getElementById('side-people');
  const count = document.getElementById('people-count');
  const total = state.participants.size + 1;
  if (count) count.textContent = String(total);
  if (!body) return;
  const rows: string[] = [];
  const selfState = state.listenOnly ? 'listen-only' : state.talkMode === 'ptt' ? 'walkie' : 'open mic';
  rows.push(personRow(`${state.prefs.name || 'Guest'} (you)`, selfState));
  for (const p of state.participants.values()) rows.push(personRow(p.name || 'Guest', p.live ? 'talking' : p.mode === 'ptt' ? 'walkie' : 'muted'));
  body.innerHTML = rows.join('');
}

function personRow(name: string, stateLabel: string): string {
  return `<div class="person"><span class="avatar sm">${escapeHtml(initials(name))}</span><span class="person-name">${escapeHtml(name)}</span><span class="person-state">${escapeHtml(stateLabel)}</span></div>`;
}

// ─── Recording ───────────────────────────────────────────────────────────
async function toggleRecord(): Promise<void> {
  if (!state.audioCtx) {
    toast('Recording needs audio support', 'err');
    return;
  }
  if (state.recording) {
    await stopRecording();
    return;
  }
  try {
    state.recorder = new CallRecorder(state.audioCtx);
    state.recordStartedAt = Date.now();
    state.recorder.start(recorderAudio());
    state.recording = true;
    setCtlActive('btn-record', true);
    updateSelfCard();
    broadcast();
    toast('Recording — saved to your device only', 'ok');
    emit('record', 'ok', 'Recording started (local only)');
  } catch (err) {
    toast('Could not start recording', 'err');
    emit('record', 'err', `Recording failed: ${(err as Error).message}`);
  }
}

async function stopRecording(): Promise<void> {
  if (!state.recorder) return;
  state.recording = false;
  setCtlActive('btn-record', false);
  updateSelfCard();
  broadcast();
  const blob = await state.recorder.stop();
  state.recorder = null;
  emit('record', 'ok', 'Recording finished', { size: `${(blob.size / 1048576).toFixed(2)} MB` });
  presentRecording(blob);
}

function recorderAudio(): MediaStream[] {
  const streams: MediaStream[] = [];
  // Record what leaves this device: our mic goes in whenever it isn't listen-only,
  // even in PTT idle, so our own turns land in the file too.
  if (state.localStream && !state.listenOnly) streams.push(state.localStream);
  for (const p of state.participants.values()) if (p.stream) streams.push(p.stream);
  return streams;
}
function refreshRecorderAudio(): void {
  if (state.recording && state.recorder) state.recorder.syncAudio(recorderAudio());
}

function presentRecording(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const ext = extForMime(blob.type || 'audio/webm');
  const fname = `talkwell-call-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${ext}`;
  const canShare = typeof navigator.share === 'function' && typeof navigator.canShare === 'function';
  openModal(
    'Your recording',
    `<p>Recorded entirely on your device — it was never uploaded.</p>
     <audio class="rec-preview" controls src="${url}"></audio>
     <div class="modal-actions">
       <a class="btn-primary" href="${url}" download="${escapeHtml(fname)}">Download .${ext}</a>
       ${canShare ? `<button type="button" class="btn-secondary" id="rec-share">Share…</button>` : ''}
     </div>
     <p class="muted small">${escapeHtml((blob.size / 1048576).toFixed(2))} MB · ${escapeHtml(blob.type || 'audio/webm')}</p>`,
  );
  document.getElementById('rec-share')?.addEventListener('click', async () => {
    try {
      const file = new File([blob], fname, { type: blob.type || 'audio/webm' });
      if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], title: 'Talkwell recording' });
      else toast('Sharing not available here', 'err');
    } catch {
      /* cancelled */
    }
  });
}

function broadcast(): void {
  state.controller?.broadcastPresence(currentPresence());
}

function setCtlActive(id: string, active: boolean): void {
  document.getElementById(id)?.classList.toggle('active', active);
}

// ─── Side panel + chat ───────────────────────────────────────────────────
function openSide(which: 'people' | 'chat'): void {
  const panel = document.getElementById('sidepanel') as HTMLElement;
  panel.hidden = false;
  document.querySelectorAll<HTMLElement>('.side-tab[data-side]').forEach((t) => t.classList.toggle('on', t.dataset.side === which));
  document.getElementById('side-people')!.hidden = which !== 'people';
  document.getElementById('side-chat')!.hidden = which !== 'chat';
  if (which === 'people') refreshPeople();
  if (which === 'chat') document.getElementById('chat-input')?.focus();
}
function closeSide(): void {
  (document.getElementById('sidepanel') as HTMLElement).hidden = true;
}

function onChatSubmit(e: Event): void {
  e.preventDefault();
  const input = document.getElementById('chat-input') as HTMLInputElement;
  const text = input.value.trim();
  if (!text) return;
  state.controller?.sendMessage(text);
  appendChat(state.prefs.name || 'You', text, true);
  input.value = '';
}
function onChat(id: string, text: string): void {
  const p = state.participants.get(id);
  appendChat(p?.name || 'Guest', text, false);
  if ((document.getElementById('sidepanel') as HTMLElement).hidden) toast('New message', 'info');
  emit('peer', 'info', 'Chat message received', { peer: short(id) });
}
function appendChat(name: string, text: string, mine: boolean): void {
  const log = document.getElementById('chat-log');
  if (!log) return;
  const row = document.createElement('div');
  row.className = `chat-msg${mine ? ' mine' : ''}`;
  row.innerHTML = `<span class="chat-name"></span><span class="chat-text"></span>`;
  (row.querySelector('.chat-name') as HTMLElement).textContent = mine ? 'You' : name;
  (row.querySelector('.chat-text') as HTMLElement).textContent = text;
  log.appendChild(row);
  log.scrollTop = log.scrollHeight;
}

// ─── Invite modal ────────────────────────────────────────────────────────
function openInviteModal(): void {
  const url = currentInviteUrl(state.room, state.pass, state.includePass);
  openModal(
    'Invite people',
    `<p class="muted small">Send this link. Whoever opens it joins your call — no account, no download.</p>
     <div class="invite-url"><input type="text" id="invite-input" readonly value="${escapeHtml(url)}" /><button type="button" class="icon-btn" id="invite-copy" title="Copy">${icon('copy')}</button></div>
     <div class="invite-actions">
       <button type="button" class="btn-secondary" id="invite-share">Share…</button>
       <button type="button" class="btn-secondary" id="invite-qr">Show QR</button>
     </div>
     <img id="invite-qr-img" class="qr-img" alt="QR code for the invite link" hidden />
     <label class="check-row"><input type="checkbox" id="invite-includepass" ${state.includePass ? 'checked' : ''} /> Include the passphrase in the link <span class="muted small">(convenient — anyone with the link can join). Uncheck for a link you can share publicly, then read the four words aloud instead.</span></label>`,
  );
  const input = document.getElementById('invite-input') as HTMLInputElement;
  document.getElementById('invite-copy')?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(input.value);
      toast('Invite link copied', 'ok');
    } catch {
      input.select();
      document.execCommand('copy');
      toast('Invite link copied', 'ok');
    }
  });
  document.getElementById('invite-share')?.addEventListener('click', async () => {
    try {
      if (typeof navigator.share === 'function') await navigator.share({ title: 'Join my Talkwell call', url: input.value });
      else {
        await navigator.clipboard.writeText(input.value);
        toast('Link copied', 'ok');
      }
    } catch {
      /* cancelled */
    }
  });
  document.getElementById('invite-qr')?.addEventListener('click', async () => {
    const img = document.getElementById('invite-qr-img') as HTMLImageElement;
    if (!img.hidden) {
      img.hidden = true;
      return;
    }
    try {
      img.src = await QRCode.toDataURL(input.value, { margin: 1, width: 240, color: { dark: '#0b0a12', light: '#ffffff' } });
      img.hidden = false;
    } catch {
      toast('Could not render QR', 'err');
    }
  });
  document.getElementById('invite-includepass')?.addEventListener('change', (e) => {
    state.includePass = (e.target as HTMLInputElement).checked;
    input.value = currentInviteUrl(state.room, state.pass, state.includePass);
    writeRoomHash(state.room, state.pass, state.includePass);
    (document.getElementById('invite-qr-img') as HTMLImageElement).hidden = true;
  });
}

// ─── Loop, wake lock, teardown ───────────────────────────────────────────
function startLoop(): void {
  if (state.loop) clearInterval(state.loop);
  state.loop = setInterval(() => {
    for (const [id, card] of state.cards) {
      const raw = card.meter?.read() ?? 0;
      let live: boolean;
      let level: number;
      if (id === 'self') {
        live = state.transmitting;
        level = live ? raw : 0;
      } else {
        const p = state.participants.get(id);
        live = !!p?.live;
        level = raw; // a non-transmitting peer's track is disabled → silence → ~0
      }
      card.el.style.setProperty('--level', String(level));
      card.el.classList.toggle('speaking', live && isSpeaking(level));
    }
    const timer = document.getElementById('call-timer');
    if (timer && state.callStartedAt) {
      const base = fmtDur(Math.floor((Date.now() - state.callStartedAt) / 1000));
      timer.textContent = state.recording ? `● ${fmtDur(Math.floor((Date.now() - state.recordStartedAt) / 1000))}` : base;
      timer.classList.toggle('rec', state.recording);
    }
  }, 100);
}

async function acquireWakeLock(): Promise<void> {
  try {
    if ('wakeLock' in navigator) state.wakeLock = await navigator.wakeLock.request('screen');
  } catch {
    /* not critical */
  }
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && state.callStartedAt && !state.wakeLock) void acquireWakeLock();
});

function leaveCall(): void {
  emit('system', 'info', 'Leaving call');
  if (state.recording) void stopRecording();
  if (state.loop) clearInterval(state.loop);
  state.loop = null;
  for (const card of state.cards.values()) {
    card.meter?.close();
    if (card.audio) card.audio.srcObject = null;
  }
  state.cards.clear();
  state.controller?.leave();
  state.controller = null;
  stopStream(state.localStream);
  state.localStream = null;
  state.participants.clear();
  try {
    state.wakeLock?.release();
  } catch {
    /* ignore */
  }
  state.wakeLock = null;
  try {
    void state.audioCtx?.close();
  } catch {
    /* ignore */
  }
  state.audioCtx = null;
  state.callStartedAt = 0;
  state.transmitting = false;
  state.held = false;
  state.muted = false;
  closeModal();
  const view = document.getElementById('view')!;
  view.classList.remove('in-call');
  // Reset to a fresh lobby to start again.
  state.mode = 'create';
  state.room = generateRoomId();
  state.pass = generatePassphrase();
  state.listenOnly = false;
  history.replaceState(null, '', location.pathname + location.search);
  renderLobby();
  toast('You left the call', 'info');
}

function short(id: string): string {
  return id.slice(0, 6);
}

window.addEventListener('pagehide', () => {
  state.controller?.leave();
  stopStream(state.localStream);
});

boot();
